import React, {useCallback, useEffect, useState} from 'react';
import {Box, Stack, TextField} from '@mui/material';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';

const PreviousSchoolName = ({
                                index,
                                componentId,
                                fieldKey,
                                value,
                                onChange,
                                descriptionHtml,
                                onDescriptionHtmlChange,
                                commitDescriptionHtml,
                                enableValidation,
                                isEditMode,
                                isFormArchived,
                                readOnly,
                                placeholderValues,
                                accessLevel,
                                submitMode,
                                error,
                                audience,
                                recipientType,
                                linkedToStudents,
                                linkedToEmployees,
                            }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    return (
        <Box key={`PreviousSchoolName_${componentId}`} sx={{mt: 2}}>
            {isEditMode
                ? <>
                    <RichEditor
                        currentDescription={draftDescriptionHtml}
                        setCurrentDescription={handleDraftChange}
                        isFormLive={isFormArchived}
                        accessLevel={accessLevel}
                        audience={audience}
                        recipientType={recipientType}
                        linkedToStudents={linkedToStudents}
                        linkedToEmployees={linkedToEmployees}
                        placeholderValues={placeholderValues}
                        onFocus={() => setIsEditorFocused(true)}
                        onBlur={handleBlur}
                    />

                    <Stack
                        flexDirection="row"
                        justifyContent="space-between"
                        gap={3} mb={2}
                    >
                        <RequiredCheckbox
                            enableValidation={enableValidation}
                            index={index}
                            onChange={onChange}
                            isFormArchived={isFormArchived}
                        />
                    </Stack>
                </>
                : <>
                    <RichEditorResultText
                        id={componentId}
                        description={descriptionHtml}
                        placeholderValues={placeholderValues}
                        readOnly={readOnly}
                        enableValidation={enableValidation}
                        audience={audience}
                        recipientType={recipientType}
                        linkedToStudents={linkedToStudents}
                        linkedToEmployees={linkedToEmployees}
                        submitMode={submitMode}
                    />
                    {!readOnly
                        ? <TextField
                            fullWidth
                            value={value || ''}
                            required={enableValidation}
                            disabled={!submitMode}
                            helperText={error}
                            error={!!error}
                            onChange={e => onChange({
                                id: componentId,
                                value: e.target.value
                            })}
                            type="text"
                        />
                        : <Typography sx={{
                            maxWidth: '100%',
                            width: {xs: '80vw', sm: '60vw', md: '45vw', lg: '45vw', xl: '45vw'},
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                        }}>
                            {value}
                        </Typography>
                    }
                </>
            }
        </Box>
    );
};

PreviousSchoolName.propTypes = {
    componentId: PropTypes.string,
    fieldKey: PropTypes.string,
    value: PropTypes.string,
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    enableValidation: PropTypes.bool,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    submitMode: PropTypes.bool,
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
};

export default PreviousSchoolName;
